/**
 * InviteToCallModal - Modal to invite users to an ongoing call
 * Supports both internal users (from chat) and external guests (via email link)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authHeaders } from '../../utils/authHeaders';
import './VideoCall.css';

// How long to wait for the server to acknowledge an invite before calling it failed.
// Without an ack the modal cannot tell "invited" from "nobody was listening".
const INVITE_ACK_TIMEOUT_MS = 8000;

function InviteToCallModal({ isOpen, onClose, onInvite, sessionId, callId, currentParticipants = [], socket, userId }) {
    const { t } = useTranslation();

    // Tab state
    const [activeTab, setActiveTab] = useState('internal'); // 'internal' or 'external'

    // Internal invite state
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [internalError, setInternalError] = useState(null);

    // External invite state
    const [externalEmail, setExternalEmail] = useState('');
    const [externalName, setExternalName] = useState('');
    const [creatingInvite, setCreatingInvite] = useState(false);
    const [inviteLink, setInviteLink] = useState(null);
    const [inviteError, setInviteError] = useState(null);
    const [copiedLink, setCopiedLink] = useState(false);

    // Fetch session participants when modal opens
    useEffect(() => {
        if (isOpen && sessionId) {
            fetchSessionParticipants();
        }
        // Reset external invite state when modal opens
        if (isOpen) {
            setInviteLink(null);
            setExternalEmail('');
            setExternalName('');
            setInviteError(null);
            setInternalError(null);
        }
    }, [isOpen, sessionId]);

    const fetchSessionParticipants = async () => {
        setLoading(true);
        setInternalError(null);
        try {
            const response = await fetch(`/api/ai/user-chat/sessions/${sessionId}/participants`, { headers: authHeaders() });
            if (response.ok) {
                const data = await response.json();
                const currentUserIds = currentParticipants.map(p => p.id);
                const available = (data.participants || []).filter(p => !currentUserIds.includes(p.id));
                setAvailableUsers(available);
            } else {
                // An empty list and a failed lookup are not the same thing: without this the
                // modal claims everyone is already on the call
                console.error('[VideoCall] Failed to load session participants:', response.status);
                setAvailableUsers([]);
                setInternalError(t('aiChat.calls.participantsUnavailable'));
            }
        } catch (error) {
            console.error('Error fetching participants:', error);
            setAvailableUsers([]);
            setInternalError(t('aiChat.calls.participantsUnavailable'));
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            }
            return [...prev, userId];
        });
    };

    const userNameFor = (targetUserId) => {
        const user = availableUsers.find(u => u.id === targetUserId);
        return user?.name || String(targetUserId);
    };

    const handleInternalInvite = async () => {
        if (selectedUsers.length === 0) return;

        setInternalError(null);

        if (!socket || !socket.connected) {
            setInternalError(t('aiChat.calls.inviteNotConnected'));
            return;
        }

        setInviting(true);
        try {
            // Every invite is acknowledged by the server. Nothing else tells this modal
            // whether anyone was actually rung - an unacknowledged emit is dropped in
            // silence, and reporting that as success is how invited people never arrive.
            const results = await Promise.all(selectedUsers.map(targetUserId => (
                new Promise(resolve => {
                    socket.timeout(INVITE_ACK_TIMEOUT_MS).emit('call-invite-user', {
                        callId,
                        sessionId,
                        targetUserId,
                        invitedByUserId: userId
                    }, (timeoutError, ack) => {
                        if (timeoutError || !ack?.success) {
                            console.error('[VideoCall] Invite failed for user', targetUserId, timeoutError || ack);
                            // failCallEvent spreads its details onto the ack, so the reason
                            // code sits at the top level (undefined on a timeout, where there
                            // is no ack at all).
                            resolve({ targetUserId, ok: false, code: ack?.code });
                        } else {
                            resolve({ targetUserId, ok: true });
                        }
                    });
                })
            )));

            const failed = results.filter(r => !r.ok);
            const invited = results.filter(r => r.ok).map(r => r.targetUserId);

            if (invited.length > 0) {
                onInvite?.(invited);
                setSelectedUsers(prev => prev.filter(id => !invited.includes(id)));
            }

            if (failed.length > 0) {
                // "Not online" is the one refusal the engineer can act on - chase them by
                // another route rather than retry - and the generic wording hides it behind
                // "they were not notified", which reads like a transient glitch. Only say it
                // when it accounts for every failure; a mixed batch falls back to the generic
                // message so no failed invite goes unlisted.
                const namesOf = list => list.map(r => userNameFor(r.targetUserId)).join(', ');
                const offline = failed.filter(r => r.code === 'invitee_offline');
                setInternalError(offline.length === failed.length
                    ? t('aiChat.calls.inviteOffline', { names: namesOf(offline) })
                    : t('aiChat.calls.inviteFailedFor', { names: namesOf(failed) }));
                return;
            }

            onClose();
        } catch (error) {
            console.error('Error inviting users:', error);
            setInternalError(t('aiChat.calls.inviteFailed'));
        } finally {
            setInviting(false);
        }
    };

    const handleExternalInvite = async (e) => {
        e.preventDefault();
        setInviteError(null);

        if (!callId) {
            setInviteError(t('aiChat.calls.noActiveCall'));
            return;
        }

        setCreatingInvite(true);
        try {
            const response = await fetch(`/api/ai/video-call/${callId}/guest-invite`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    email: externalEmail || null,
                    name: externalName || null,
                    invitedByUserId: userId
                })
            });

            const data = await response.json();

            if (!response.ok) {
                setInviteError(data.error || t('aiChat.calls.failedToCreateInvite'));
                return;
            }

            setInviteLink(data.invite);
        } catch (error) {
            console.error('Error creating external invite:', error);
            setInviteError(t('aiChat.calls.failedToCreateInviteRetry'));
        } finally {
            setCreatingInvite(false);
        }
    };

    const copyToClipboard = useCallback(async (text) => {
        try {
            // Try modern Clipboard API first (requires secure context)
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for popup windows / non-secure contexts
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.left = '-9999px';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, []);

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const resetExternalInvite = () => {
        setInviteLink(null);
        setExternalEmail('');
        setExternalName('');
        setInviteError(null);
    };

    if (!isOpen) return null;

    return (
        <div className="invite-modal-overlay" onClick={onClose}>
            <div className="invite-modal invite-modal-tabs" onClick={e => e.stopPropagation()}>
                <div className="invite-modal-header">
                    <h3>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <line x1="20" y1="8" x2="20" y2="14" />
                            <line x1="23" y1="11" x2="17" y2="11" />
                        </svg>
                        {t('aiChat.calls.inviteToCall')}
                    </h3>
                    <button className="invite-modal-close" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="invite-tabs">
                    <button
                        className={`invite-tab ${activeTab === 'internal' ? 'active' : ''}`}
                        onClick={() => setActiveTab('internal')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                        </svg>
                        {t('aiChat.calls.teamMembers')}
                    </button>
                    <button
                        className={`invite-tab ${activeTab === 'external' ? 'active' : ''}`}
                        onClick={() => setActiveTab('external')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="2" y1="12" x2="22" y2="12" />
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                        {t('aiChat.calls.externalGuest')}
                    </button>
                </div>

                {/* Internal Invite Tab */}
                {activeTab === 'internal' && (
                    <>
                        <div className="invite-modal-content">
                            {loading ? (
                                <div className="invite-loading">
                                    <div className="loading-spinner" />
                                    <span>{t('aiChat.calls.loadingParticipants')}</span>
                                </div>
                            ) : availableUsers.length === 0 ? (
                                <div className="no-users-to-invite">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                    </svg>
                                    <span>{internalError || t('aiChat.calls.everyoneAlreadyOnCall')}</span>
                                </div>
                            ) : (
                                <div className="invite-user-list">
                                    {availableUsers.map(user => (
                                        <div
                                            key={user.id}
                                            className={`invite-user-item ${selectedUsers.includes(user.id) ? 'selected' : ''}`}
                                            onClick={() => toggleUser(user.id)}
                                        >
                                            <div className="invite-user-checkbox">
                                                {selectedUsers.includes(user.id) ? (
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                        <rect x="3" y="3" width="18" height="18" rx="3" fill="var(--call-accent)" />
                                                        <path d="M9 12l2 2 4-4" stroke="var(--call-on-accent)" strokeWidth="2" fill="none" />
                                                    </svg>
                                                ) : (
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="3" y="3" width="18" height="18" rx="3" />
                                                    </svg>
                                                )}
                                            </div>
                                            <div className="invite-user-avatar">
                                                {getInitials(user.name)}
                                            </div>
                                            <div className="invite-user-info">
                                                <span className="invite-user-name">{user.name}</span>
                                                {user.email && (
                                                    <span className="invite-user-email">{user.email}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {internalError && availableUsers.length > 0 && (
                                <div className="form-error">{internalError}</div>
                            )}
                        </div>

                        <div className="invite-modal-footer">
                            <button className="invite-cancel-btn" onClick={onClose}>
                                {t('aiChat.modals.cancel')}
                            </button>
                            <button
                                className="invite-send-btn"
                                onClick={handleInternalInvite}
                                disabled={selectedUsers.length === 0 || inviting}
                            >
                                {inviting ? (
                                    <>
                                        <div className="btn-spinner" />
                                        {t('aiChat.modals.inviting')}
                                    </>
                                ) : (
                                    <>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 2L11 13" />
                                            <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                                        </svg>
                                        {selectedUsers.length > 0
                                            ? t('aiChat.calls.inviteCount', { count: selectedUsers.length })
                                            : t('aiChat.calls.invite')}
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* External Invite Tab */}
                {activeTab === 'external' && (
                    <>
                        <div className="invite-modal-content external-invite-content">
                            {inviteLink ? (
                                /* Invite Created - Show Link */
                                <div className="invite-link-created">
                                    <div className="invite-success-icon">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    </div>
                                    <h4>{t('aiChat.calls.inviteLinkCreated')}</h4>
                                    <p>{t('aiChat.calls.shareThisLink')}</p>

                                    <div className="invite-link-box">
                                        <input
                                            type="text"
                                            value={inviteLink.joinUrl}
                                            readOnly
                                            className="invite-link-input"
                                        />
                                        <button
                                            className="copy-link-btn"
                                            onClick={() => copyToClipboard(inviteLink.joinUrl)}
                                        >
                                            {copiedLink ? (
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            ) : (
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                </svg>
                                            )}
                                            {copiedLink ? t('aiChat.calls.copied') : t('aiChat.calls.copy')}
                                        </button>
                                    </div>

                                    <div className="invite-pin-display">
                                        <span className="pin-label">{t('aiChat.calls.meetingPin')}</span>
                                        <span className="pin-value">{inviteLink.meetingPin}</span>
                                    </div>

                                    <button className="create-another-btn" onClick={resetExternalInvite}>
                                        {t('aiChat.calls.createAnotherInvite')}
                                    </button>
                                </div>
                            ) : (
                                /* Invite Form */
                                <form onSubmit={handleExternalInvite} className="external-invite-form">
                                    <p className="external-invite-desc">
                                        {t('aiChat.calls.externalInviteDesc')}
                                    </p>

                                    <div className="form-group">
                                        <label htmlFor="guestName">{t('aiChat.calls.guestNameOptional')}</label>
                                        <input
                                            type="text"
                                            id="guestName"
                                            value={externalName}
                                            onChange={(e) => setExternalName(e.target.value)}
                                            placeholder={t('aiChat.calls.guestNamePlaceholder')}
                                            disabled={creatingInvite}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="guestEmail">{t('aiChat.calls.guestEmailOptional')}</label>
                                        <input
                                            type="email"
                                            id="guestEmail"
                                            value={externalEmail}
                                            onChange={(e) => setExternalEmail(e.target.value)}
                                            placeholder={t('aiChat.calls.guestEmailPlaceholder')}
                                            disabled={creatingInvite}
                                        />
                                        <span className="form-hint">{t('aiChat.calls.guestEmailHint')}</span>
                                    </div>

                                    {inviteError && (
                                        <div className="form-error">{inviteError}</div>
                                    )}

                                    <button
                                        type="submit"
                                        className="create-invite-btn"
                                        disabled={creatingInvite}
                                    >
                                        {creatingInvite ? (
                                            <>
                                                <div className="btn-spinner" />
                                                {t('aiChat.calls.creating')}
                                            </>
                                        ) : (
                                            <>
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                </svg>
                                                {t('aiChat.calls.createInviteLink')}
                                            </>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>

                        <div className="invite-modal-footer">
                            <button className="invite-cancel-btn" onClick={onClose}>
                                {inviteLink ? t('aiChat.calls.done') : t('aiChat.modals.cancel')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default InviteToCallModal;
